import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Sparkles, TrendingUp, Zap } from 'lucide-react';

export function StudyEfficiencyCalculator() {
  const [hoursPerDay, setHoursPerDay] = useState([4]);

  const dailyHours = hoursPerDay[0];
  const weeklyHours = dailyHours * 6; // Considering 6 days/week
  
  // Hypothesized gains: 30% more effective retention = 1.3x efficiency
  const traditionalEffectiveness = weeklyHours * 0.5; // Only 50% retained traditionally
  const focusTrackEffectiveness = weeklyHours * 0.8; // 80% retained with Spaced Repetition + Focus
  
  const increase = Math.round(((focusTrackEffectiveness - traditionalEffectiveness) / traditionalEffectiveness) * 100);
  const hoursSavedPerYear = Math.round((weeklyHours * 0.3) * 52); // Saving 30% time to same result

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="container px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          <div className="space-y-6">
            <h2 className="text-3xl font-bold md:text-4xl tracking-tight">
              Pare de desperdiçar sua <span className="text-primary">energia mental</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              O método tradicional de estudo ignora a <span className="text-foreground font-semibold italic">Curva de Esquecimento</span>. 
              Com o FocusTrack, você estuda menos tempo com resultados infinitamente superiores.
            </p>
            
            <Card className="border shadow-xl bg-card/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Quantas horas você estuda por dia?</CardTitle>
                <div className="text-4xl font-extrabold text-primary py-2">{dailyHours}h</div>
              </CardHeader>
              <CardContent className="space-y-6">
                <Slider
                  value={hoursPerDay}
                  onValueChange={setHoursPerDay}
                  max={12}
                  step={0.5}
                  className="py-4"
                  aria-label="Horas de estudo por dia"
                />
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <TrendingUp className="h-4 w-4" />
                  <span>Calculado baseando-se em 6 dias de estudo semanal</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={dailyHours}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.05 }}
                className="grid gap-4"
              >
                <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 rounded-2xl p-8 space-y-6">
                  <div className="flex items-center gap-3 text-primary">
                    <Zap className="h-6 w-6 fill-current" />
                    <span className="text-lg font-bold">O Efeito FocusTrack</span>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Aumento de Retenção</p>
                      <p className="text-5xl font-black text-foreground">+{increase}%</p>
                    </div>
                    
                    <div className="h-px bg-primary/10 w-full" />
                    
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Tempo "Recuperado" p/ Ano</p>
                      <div className="flex items-baseline gap-2">
                        <p className="text-4xl font-bold text-primary">{hoursSavedPerYear}h</p>
                        <span className="text-xs text-muted-foreground">equi. a ~{Math.round(hoursSavedPerYear/24)} dias</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground pt-4 border-t border-primary/5">
                    * Estimativa baseada no sistema de revisões espaçadas de Ebbinghaus e técnica Pomodoro.
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Decorative Sparkle */}
            <div className="absolute -top-4 -right-4 bg-background border shadow-lg rounded-full p-2 h-12 w-12 flex items-center justify-center animate-bounce">
              <Sparkles className="h-6 w-6 text-yellow-500 fill-current" />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
